import { isNil } from 'lodash';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { filterValidationKeys } from '../../constants';
import { FilterInputComponentRefType } from '../../filter-types';
import { useFiltersContext } from '../../filters-context';
import { useFilterValidation } from '../../useFilterValidation';
import { PickOperatorBtn } from '../PickOperatorBtn';
import { useOperator } from '../useOperator';
import { MultiSelectFields } from './MultiSelectFields';
import { TextFilter, TextFilterValue } from './TextFilter';

export const TextFilterInput = forwardRef<FilterInputComponentRefType, { filter: TextFilter }>(({ filter }, ref) => {
  const { inputProps, operator: initialOperator, operatorOptions, values } = filter;
  const [allValues, setAllValues] = useState<TextFilterValue[]>(values.length === 0 ? [''] : values);

  const { applySingleNewFilter, updateAppliedFilter, isFilterApplied } = useFiltersContext();

  const { operator, setOperator } = useOperator(initialOperator);

  const onSubmit = useCallback(() => {
    if (isFilterApplied(filter.id)) {
      updateAppliedFilter(filter.id, { values: allValues, operator });
      return;
    }

    const clone = Object.assign({}, filter);
    clone.values = allValues;
    clone.operator = operator;
    applySingleNewFilter(clone);
  }, [allValues, applySingleNewFilter, filter, isFilterApplied, operator, updateAppliedFilter]);

  const { handleSubmit, error } = useFilterValidation({
    value: allValues,
    onSubmit: onSubmit,
    validationFunc: (val) => {
      const hasUndefinedValues = val.filter((item) => {
        return isNil(item) || (typeof item === 'string' && !item.replace(/\s/g, '').length);
      });

      if (hasUndefinedValues.length > 0 || val.length === 0) {
        return filterValidationKeys.required;
      }
    },
  });

  useImperativeHandle(ref, () => ({ submit: handleSubmit, error }));

  const placeholder = useMemo(() => {
    const p = inputProps?.placeholder;
    if (!p) return '';
    if (typeof p === 'string') {
      return p;
    }

    if (typeof p === 'function') {
      return p(operator);
    }
  }, [inputProps?.placeholder, operator]);

  return (
    <div className='space-y-3 bg-white overflow-hidden flex flex-col'>
      <PickOperatorBtn onOperatorChange={setOperator} operatorOptions={operatorOptions} selectedOperator={operator} />

      <MultiSelectFields
        filter={filter}
        placeholder={placeholder}
        error={error}
        setAllValues={(value) => setAllValues(value)}
        allValues={allValues}
      />
    </div>
  );
});
